/**
 * 단지 비교 페이지 — /compare (2026-07-07 경쟁사 격차 해소 로드맵 #10).
 *
 * 비교함(localStorage)에 담긴 2~4개 단지를 나란히 비교한다.
 * - 모바일: 가로 스크롤(라벨 컬럼 sticky) / 데스크톱: 한 화면 그리드
 * - 개인화 페이지이므로 robots noindex.
 */
import { propertyPyeongRange } from '../domain/property/pyeong';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { propertyApi, Property } from '../services/api';
import { useCompare } from '../domain/compare/useCompare';
import { clearCompare, removeCompare } from '../domain/compare/compareStore';
import { getPropertyUrl, formatAreaRange, formatMoveInDate, cleanFieldText, formatSubscriptionDate } from '../utils/formatters';
import {
  formatPriceForDisplay,
  formatUnits,
  maskPropertyStatus,
  sanitizeContentText,
} from '../domain/property/formatters';
import { EmptyState } from '../design-system/components';

// 비교 대상 개수별 grid-template (라벨 컬럼 + 단지 컬럼). 정적 문자열이라 Tailwind JIT 인식.
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-[72px_minmax(150px,1fr)]',
  2: 'grid-cols-[72px_minmax(150px,1fr)_minmax(150px,1fr)]',
  3: 'grid-cols-[72px_minmax(150px,1fr)_minmax(150px,1fr)_minmax(150px,1fr)]',
  4: 'grid-cols-[72px_minmax(150px,1fr)_minmax(150px,1fr)_minmax(150px,1fr)_minmax(150px,1fr)]',
};

// 주소 축약 — 시/도·구·동 앞 3토큰만.
function abbrevAddress(addr?: string): string {
  if (!addr) return '-';
  const parts = addr.trim().split(/\s+/);
  return parts.slice(0, 3).join(' ') || '-';
}

interface CompareRow {
  label: string;
  render: (p: Property) => React.ReactNode;
}

// 공급 평형 이름만 (정확 금액은 가격 공개 원칙상 비교표에 싣지 않는다 — 구간만)
function typeNames(p: Property): string {
  const names = (p.type_prices || []).map((t) => t.type_name).filter(Boolean);
  const uniq = Array.from(new Set(names));
  return uniq.length ? uniq.join(' · ') : '-';
}

// 2026-09-25: '분양상태'는 정책상 분양예정만 표시·나머지 마스킹이라 항상 '-' → 빼고,
//   분양예정일 때만 배지로 보여준다. 모바일 비교에서 '상세 내용'이 부족하다는 피드백으로 항목 추가.
const ROWS: CompareRow[] = [
  { label: '위치', render: (p) => abbrevAddress(p.address) },
  { label: '총세대수', render: (p) => formatUnits(p) || '-' },
  { label: '평형(공급)', render: (p) => propertyPyeongRange(p) || '-' },
  { label: '전용면적', render: (p) => formatAreaRange(p) || '-' },
  { label: '분양가', render: (p) => formatPriceForDisplay(p.min_price, p.max_price) },
  { label: '공급평형', render: (p) => typeNames(p) },
  { label: '청약일정', render: (p) => formatSubscriptionDate(p) || '-' },
  { label: '입주예정', render: (p) => formatMoveInDate(p.move_in_date) || '-' },
  { label: '규모', render: (p) => cleanFieldText(p.building_scale) || '-' },
  { label: '주차', render: (p) => (p.parking_spaces ? `${p.parking_spaces.toLocaleString()}대` : '-') },
  { label: '시공사', render: (p) => cleanFieldText(p.constructor) || '-' },
  { label: '시행사', render: (p) => cleanFieldText(p.developer) || '-' },
  {
    label: '대표혜택',
    render: (p) => {
      const t = Array.isArray(p.discount_benefits) ? p.discount_benefits[0]?.title : undefined;
      return t ? sanitizeContentText(t) : '-';
    },
  },
];

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ComparePage: React.FC = () => {
  const ids = useCompare();
  const navigate = useNavigate();
  const [items, setItems] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  const idKey = ids.join(',');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (ids.length === 0) {
        if (alive) {
          setItems([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      const results = await Promise.all(
        ids.map((id) => propertyApi.getDetail(String(id)).catch(() => null)),
      );
      if (alive) {
        setItems(results.filter((p): p is Property => p !== null));
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  useSeoMeta({
    title: '단지 비교 | 부냥',
    robots: 'noindex, follow',
  });

  const gridClass = GRID_COLS[Math.min(Math.max(items.length, 1), 4)];

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto max-w-md px-4 pt-6 pb-28 md:max-w-3xl md:px-6 md:py-8">  {/* pb-28: 모바일 하단 탭바에 마지막 줄이 가리지 않게 */}
        <nav className="mb-3 text-[12px] text-muted">
          <Link to="/" className="hover:text-ink">부냥</Link> · 단지 비교
        </nav>

        <div className="flex items-center justify-between gap-3">
          <h1 className="text-[20px] font-extrabold text-ink md:text-[24px]">
            단지 비교
            {ids.length > 0 && <span className="ml-1.5 text-[15px] font-bold text-muted">{ids.length}</span>}
          </h1>
          {ids.length > 0 && (
            <button
              type="button"
              onClick={() => clearCompare()}
              className="shrink-0 rounded-ca-lg border border-ca-line bg-frame px-3 py-1.5 text-[12px] font-bold text-body active:translate-y-[1px]"
            >
              전체 비우기
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-10 text-center text-[13px] text-muted">불러오는 중…</div>
        ) : items.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              variant="section"
              title="비교할 단지를 담아보세요"
              description="지도나 단지 상세페이지에서 비교함에 담으면 여기서 최대 4개까지 나란히 비교할 수 있어요."
              actionLabel="지도로 가기"
              onAction={() => navigate('/')}
            />
          </div>
        ) : (
          <>
            {items.length === 1 && (
              <div className="-mx-4 mt-4 md:mx-0">
                <EmptyState
                  variant="banner"
                  title="1개 더 담으면 비교가 시작돼요"
                  description="지도나 상세페이지에서 단지를 추가로 담아보세요."
                />
              </div>
            )}

            <div className="mt-5 overflow-x-auto">
              <div className={`grid ${gridClass} gap-x-2`}>
                {/* 헤더 행: 라벨 컬럼(빈칸) + 단지별 이미지/제목/제거 */}
                <div className="sticky left-0 z-10 bg-paper" />
                {items.map((p) => (
                  <div key={`head-${p.id}`} className="relative">
                    <Link to={getPropertyUrl(p)} className="block">
                      <div className="aspect-[4/3] w-full overflow-hidden rounded-ca-lg border border-ca-line bg-block">
                        {(p.image_card || p.image_url) ? (
                          <img
                            src={p.image_card || p.image_url}
                            alt={p.title}
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-[11px] text-mute2">
                            이미지 준비 중
                          </div>
                        )}
                      </div>
                      <div className="mt-1.5 line-clamp-2 text-[12.5px] font-bold leading-snug text-ink">
                        {p.title}
                      </div>
                      {maskPropertyStatus(p.status, (p as any).is_pre_registration === true, (p as any).is_reopen === true) && (
                        <span className="mt-1 inline-block rounded-ca-sm bg-accent-soft px-1.5 py-0.5 text-[10.5px] font-bold text-accent">
                          {maskPropertyStatus(p.status, (p as any).is_pre_registration === true, (p as any).is_reopen === true)}
                        </span>
                      )}
                      <span className="mt-1 block text-[11px] font-semibold text-accent">상세 보기 ›</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => removeCompare(p.id)}
                      aria-label="비교에서 제거"
                      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-ca-line bg-frame text-mute2 active:translate-y-[1px]"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                ))}

                {/* 속성 행들 */}
                {ROWS.map((row) => (
                  <React.Fragment key={row.label}>
                    <div className="sticky left-0 z-10 flex items-center border-t border-ca-line bg-paper py-2.5 text-[11px] font-semibold text-muted">
                      {row.label}
                    </div>
                    {items.map((p) => (
                      <div
                        key={`${row.label}-${p.id}`}
                        className="border-t border-ca-line py-2.5 text-[12.5px] font-medium leading-snug text-body"
                      >
                        {row.render(p)}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="text-[13px] font-bold text-accent underline">전체 분양 지도 보기 →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
